import React, { useState, useEffect } from 'react'
import './Leaderboard.css'
import { getTopScorersEvent, getEvents } from 'src/api/event'
import { downloadImageById } from 'src/api/image'
import diamond from 'src/assets/images/chest.png'
import star from 'src/assets/images/gold.png'
import circle from 'src/assets/images/silver.png'
import finalround from 'src/assets/images/finalround.png'
import mafeventlogo from 'src/assets/images/mafeventlogo.png'
import exscapeeventlogo from 'src/assets/images/exscapeeventlogo.png'
import eventleaderbg from 'src/assets/images/eventleaderbg.png'
import { Spin, Tooltip } from 'antd'

type RewardType = 'STAR' | 'CIRCLE' | 'DIAMOND'

const LeaderboardEvents: React.FC = () => {
  const rewardIcons: Record<RewardType, string> = {
    STAR: star,
    CIRCLE: circle,
    DIAMOND: diamond,
  }

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<any[]>([])
  const [eventId, setEventId] = useState('')

  const fetchEventId = async () => {
    const result = await getEvents()
    setEventId(result.data[0].id)
  }

  const extractImageId = (imageS3Path: string) => {
    try {
      const url = new URL(imageS3Path)
      const imagePath = url.searchParams.get('imagePath') || ''
      return imagePath.split('.')[0]
    } catch (e) {
      return null
    }
  }

  const formatRewards = (
    rewards: Record<string, number> = {}
  ): {
    type: RewardType
    icon: string
    count: number
  }[] => {
    const order: RewardType[] = ['DIAMOND', 'CIRCLE', 'STAR']

    return order.map((type) => ({
      type,
      icon: rewardIcons[type],
      count: rewards?.[type] ?? 0,
    }))
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getTopScorersEvent(eventId)

      const initialRows = (response?.data || []).map((item) => ({
        ...item,
        imageUrl: null,
        imageLoading: true,
      }))

      setRows(initialRows)

      initialRows.forEach(async (item, idx) => {
        if (!item.imageS3Path) {
          updateRow(idx, { imageLoading: false })
          return
        }

        try {
          const id = extractImageId(item.imageS3Path)
          if (!id) return

          const imageUrl = await downloadImageById(id)

          updateRow(idx, { imageUrl, imageLoading: false })
        } catch (error) {
          console.error('Image download failed:', error)
          updateRow(idx, { imageLoading: false })
        }
      })
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateRow = (index: number, patch: object) => {
    setRows((old) =>
      old.map((row, i) => (i === index ? { ...row, ...patch } : row))
    )
  }

  useEffect(() => {
    document.body.classList.add('leaderboard-page')

    return () => {
      document.body.classList.remove('leaderboard-page')
    }
  }, [])

  useEffect(() => {
    if (!eventId) return

    fetchData()

    const interval = setInterval(() => {
      fetchData()
    }, 30000)

    return () => clearInterval(interval)
  }, [eventId])

  useEffect(() => {
    fetchEventId()
  }, [])

  return (
    <div className="leaderboard-page-wrapper">
      <div
        className="event-leaderboard-wrapper"
        style={{
          backgroundImage: `url(${eventleaderbg})`,
          backgroundPosition: 'top center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="exscape-header">
          <img src={exscapeeventlogo} alt="Exscape" className="logo-left" />

          <img
            src={finalround}
            alt="FINAL ROUND"
            className="final-round-logo"
          />
          <div className="right-logos">
            <img src={mafeventlogo} alt="30 Years MAF" className="logo-right" />
          </div>
        </div>

        <div className="leaderboard-container">
          <div className="leaderboard-header-container">
            <div className="leaderboard-header">
              <span>Rank</span>
              <span>Name</span>
              <span>Score</span>
            </div>
            <div className="leaderboard-header">
              <span>Rank</span>
              <span>Name</span>
              <span>Score</span>
            </div>
            <div className="leaderboard-header">
              <span>Rank</span> <span>Name</span>
              <span>Score</span>
            </div>
          </div>

          <div className="leaderboard-list">
            {!loading &&
              rows.map((item) => (
                <div key={item.id} className="leaderboard-row">
                  <div className="rank-circle">{item.rank}</div>

                  <div className="player-info">
                    <div className="avatar-wrapper">
                      {item.imageLoading ? (
                        <Spin size="small" />
                      ) : (
                        <img src={item.imageUrl} className="player-avatar" />
                      )}
                    </div>

                    {item.username.length > 15 ? (
                      <Tooltip title={item.username}>
                        <span className="player-name truncate">
                          {item.username}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="player-name">{item.username}</span>
                    )}
                  </div>

                  <div className="score-box">
                    <span className="score-text">{item.points}</span>

                    <div className="reward-icons">
                      {formatRewards(item.rewards).map((r) => (
                        <div key={r.type} className="reward-item">
                          <img
                            src={r.icon}
                            className="reward-icon"
                            alt={r.type}
                          />
                          <span className="reward-count">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeaderboardEvents
